<template>
    <div :class="['navbar-header-container', { dark: theme == THEMES.DARK }]">
        <div class="container">
            <nav class="navbar-header">
                <div class="container-fluid header-link-container">
                    <div class="sidebar-toggle-area">
                        <button
                            v-if="!showSidebar"
                            type="button"
                            class="sidebar-expand-button"
                            @click="toggleShowSidebar"
                            aria-label="Show navigation"
                        >
                            <i class="material-icons">menu</i>
                            <span class="expand-text">Show Navigation</span>
                        </button>
                    </div>

                    <!-- Command palette trigger button (centered) -->
                    <div class="search-container">
                        <button
                            class="search-trigger"
                            type="button"
                            @click="openCommandPalette"
                            aria-label="Open command palette"
                        >
                            <i class="material-icons">search</i>
                            <span class="search-placeholder">Search or type a command</span>
                            <div class="keyboard-shortcut">
                                <span>⌘</span><span>K</span>
                            </div>
                        </button>
                    </div>

                    <div class="header-actions">
                        <!-- Compact Scanner Status -->
                        <button
                            type="button"
                            class="scanner-indicator"
                            @click="openScannerPanel"
                            :title="scannerStatus"
                            :aria-label="`Scanner status: ${scannerStatus}`"
                        >
                            <i class="material-icons" :class="scannerStatusClass">{{ scannerIcon }}</i>
                        </button>
                        <!-- Theme switch hidden intentionally -->
                        <div class="divider"></div>
                        <div class="profile-wrapper" v-if="user">
                            <router-link
                                to="/my-profile"
                                class="profile-info"
                            >
                                <div class="avatar-container">
                                    <span class="avatar-text">{{ getUserInitials }}</span>
                                </div>
                            </router-link>
                        </div>
                        <button class="logout-button" type="button" @click="logoutUtil" title="Logout">
                            <i class="material-icons">logout</i>
                        </button>
                    </div>
                </div>
            </nav>
        </div>

        <div class="page-title-container">
            <div class="container">
                <h1 class="page-title">{{ pageTitle }}</h1>
            </div>
        </div>

        <!-- Command Palette Modal -->
        <transition name="fade">
            <CommandPalette
                v-if="showCommandPalette"
                :search-query="searchQuery"
                :filtered-results="filteredResults"
                :selected-index="selectedIndex"
                :pages="pages"
                :is-empty-results="isEmptyResults"
                :get-item-index="getItemIndex"
                @close="closeCommandPalette"
                @update-search-query="searchQuery = $event"
                @perform-search="performSearch"
                @navigate-results="navigateResults"
                @select-result="selectResult"
                @navigate-to="navigateTo"
                @navigate-to-customer="navigateToCustomer"
                @navigate-to-product="navigateToProduct"
                @select-index="selectedIndex = $event"
            />
        </transition>

        <!-- Scanner Panel Modal -->
        <transition name="fade">
            <div class="scanner-panel-overlay" v-if="showScannerPanel" @click="closeScannerPanel">
                <div class="scanner-panel" @click.stop>
                    <ScannerStatus />
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import { mapActions, mapState } from "vuex"
import ScannerStatus from "../UI/ScannerStatus.vue"
import CommandPalette from "./CommandPalette.vue"
import { http } from "@/utils/http"
import { ROUTE_PATHS, productDetailPath, customerEditPath } from "@/router/route-paths"
import { checkHasPermission, PERMISSION_CODES } from "@/store/permission"

export default {
    data() {
        return {
            showCommandPalette: false,
            showScannerPanel: false,
            searchQuery: '',
            selectedIndex: 0,
            pages: [
                { title: 'Dashboard', path: ROUTE_PATHS.DASHBOARD, icon: 'dashboard', description: 'View business overview' },
                { title: 'Sales & Revenue Dashboard', path: ROUTE_PATHS.DASHBOARD_SALES_REVENUE, icon: 'query_stats', description: 'Review revenue, store, product, and category analytics' },
                { title: 'Inventory Operations Dashboard', path: ROUTE_PATHS.DASHBOARD_INVENTORY_OPERATIONS, icon: 'inventory', description: 'Plan low stock, aging, transfer, and inventory-risk analytics' },
                { title: 'Product & Category Dashboard', path: ROUTE_PATHS.DASHBOARD_PRODUCT_CATEGORY, icon: 'category', description: 'Plan product movement, category, brand, and price-band analytics' },
                { title: 'Compliance & Data Quality Dashboard', path: ROUTE_PATHS.DASHBOARD_COMPLIANCE_DATA_QUALITY, icon: 'rule', description: 'Plan AGCO, cleanup, and sync-health analytics' },
                { title: 'Customer & Loyalty Dashboard', path: ROUTE_PATHS.DASHBOARD_CUSTOMER_LOYALTY, icon: 'loyalty', description: 'Plan retention, repeat purchase, campaign, and loyalty analytics' },
                { title: 'Ecommerce Dashboard', path: ROUTE_PATHS.DASHBOARD_ECOMMERCE, icon: 'storefront', description: 'Plan online revenue, funnel, fulfillment, and catalog-readiness analytics' },
                { title: 'Finance & Management Dashboard', path: ROUTE_PATHS.DASHBOARD_FINANCE_MANAGEMENT, icon: 'account_balance', description: 'Plan revenue bridge, tax, cash, margin, and scorecard analytics' },
                { title: 'Customers', path: ROUTE_PATHS.CUSTOMERS, icon: 'people', description: 'Manage customer profiles' },
                { title: 'Products', path: ROUTE_PATHS.PRODUCTS, icon: 'inventory_2', description: 'Manage product catalog' },
                { title: 'Orders', path: ROUTE_PATHS.ORDERS, icon: 'shopping_cart', description: 'View and manage orders' },
                { title: 'Inventory', path: ROUTE_PATHS.INVENTORY_MANAGER, icon: 'inventory', description: 'Track inventory levels' },
                { title: 'Promotions', path: ROUTE_PATHS.PROMOTIONS, icon: 'campaign', description: 'Manage customer promotions' },
                { title: 'Customer Account Automation', path: ROUTE_PATHS.ACCOUNT_AUTOMATION, icon: 'mark_email_read', description: 'Manage signup automation' },
                { title: 'Email Templates', path: ROUTE_PATHS.EMAIL_TEMPLATES, icon: 'mail', description: 'Build transactional email designs', permission: PERMISSION_CODES.CMS_VIEW_EMAIL_TEMPLATE },
                { title: 'Newsletter Subscribers', path: ROUTE_PATHS.NEWSLETTER_SUBSCRIBERS, icon: 'mark_email_read', description: 'Review consent-backed website signups', permission: PERMISSION_CODES.CMS_VIEW_NEWSLETTER_SUBSCRIBER },
                { title: 'Loyalty Program', path: ROUTE_PATHS.LOYALTY, icon: 'loyalty', description: 'Configure loyalty earning rules' },
                { title: 'Loyalty Campaigns', path: ROUTE_PATHS.LOYALTY_CAMPAIGNS, icon: 'campaign', description: 'Manage approved bonus point campaigns' },
                { title: 'Loyalty Rewards', path: ROUTE_PATHS.LOYALTY_REWARDS, icon: 'redeem', description: 'Manage reward catalog' },
                { title: 'Loyalty Tiers', path: ROUTE_PATHS.LOYALTY_TIERS, icon: 'workspace_premium', description: 'Manage loyalty tiers' },
                { title: 'Loyalty Health', path: ROUTE_PATHS.LOYALTY_HEALTH, icon: 'monitor_heart', description: 'Review loyalty health checks' },
                { title: 'Loyalty Reconciliation', path: ROUTE_PATHS.LOYALTY_RECONCILIATION, icon: 'rule', description: 'Resolve loyalty issues' },
                { title: 'Reports', path: ROUTE_PATHS.REPORTS, icon: 'bar_chart', description: 'View business reports' },
                { title: 'Settings', path: ROUTE_PATHS.SETTINGS, icon: 'settings', description: 'Configure system settings' },
                { title: 'Profile', path: ROUTE_PATHS.MY_PROFILE, icon: 'person', description: 'Manage your profile' },
            ],
            customers: [],
            products: [],
            isSearching: false,
            searchTimer: null,
            searchRequestId: 0,
        }
    },
    computed: {
        ...mapState("auth", ["user"]),
        ...mapState("misc", ["pageTitle", "showSidebar", "theme", "THEMES"]),

        getUserInitials() {
            if (!this.user) return '';
            const firstName = this.user.first_name || '';
            const lastName = this.user.last_name || '';
            return (firstName.charAt(0) + lastName.charAt(0)).toUpperCase();
        },

        filteredResults() {
            if (!this.searchQuery) {
                return { pages: [], customers: [], products: [] };
            }

            const query = this.searchQuery.toLowerCase();

            // Filter pages
            const pages = this.pages.filter(page => {
                const canView = !page.permission || checkHasPermission({
                    isAdmin: this.user?.isAdmin,
                    permissionId: page.permission,
                    userPermissionIds: this.user?.permissionIds || [],
                })
                return canView && (
                    page.title.toLowerCase().includes(query) ||
                    page.description.toLowerCase().includes(query)
                )
            });

            const customers = this.customers.slice(0, 5);
            const products = this.products.slice(0, 5);

            return { pages, customers, products };
        },

        isEmptyResults() {
            const results = this.filteredResults;
            return results.pages.length === 0 &&
                   results.customers.length === 0 &&
                   results.products.length === 0;
        },

        totalResultsCount() {
            const results = this.filteredResults;
            return results.pages.length + results.customers.length + results.products.length;
        },

        // Scanner computed properties
        scannerStatus() {
            return this.$scanner?.connectionStatus || 'Disconnected';
        },

        scannerStatusClass() {
            const status = this.scannerStatus.toLowerCase();
            if (status.includes('connected') || status.includes('ready')) {
                return 'scanner-connected';
            } else if (status.includes('scanning') || status.includes('active')) {
                return 'scanner-active';
            } else {
                return 'scanner-disconnected';
            }
        },

        scannerIcon() {
            const status = this.scannerStatus.toLowerCase();
            if (status.includes('scanning') || status.includes('active')) {
                return 'scanner';
            } else if (status.includes('connected') || status.includes('ready')) {
                return 'qr_code_scanner';
            } else {
                return 'scanner';
            }
        }
    },
    methods: {
        ...mapActions("auth", ["logout"]),
        ...mapActions("misc", ["setData"]),

        async logoutUtil() {
            await this.logout()
            if (this.$route.path !== ROUTE_PATHS.LOGIN) {
                this.$router.push(ROUTE_PATHS.LOGIN)
            }
        },

        handleKeyDown(e) {
            // Check for Command+K (Mac) or Ctrl+K (Windows)
            if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
                e.preventDefault();
                this.openCommandPalette();
            }
        },

        toggleShowSidebar() {
            this.setData({
                showSidebar: !this.showSidebar,
            })
        },

        openCommandPalette() {
            this.showCommandPalette = true;
            this.searchQuery = '';
            this.selectedIndex = 0;
            this.performSearch();
        },

        closeCommandPalette() {
            this.showCommandPalette = false;
        },

        async fetchSearchResults(query = this.searchQuery.trim(), requestId = null) {
            if (requestId === null) {
                requestId = ++this.searchRequestId;
            }

            if (query.length < 2) {
                if (requestId === this.searchRequestId) {
                    this.customers = [];
                    this.products = [];
                    this.isSearching = false;
                }
                return;
            }

            try {
                const [customerRes, productRes] = await Promise.all([
                    http.get('/customers', {
                        params: {
                            start: 0,
                            limit: 5,
                            search: query,
                        },
                    }),
                    http.get('/items', {
                        params: {
                            page: 1,
                            limit: 5,
                            search: query,
                        },
                    }),
                ]);

                if (requestId !== this.searchRequestId) return;

                if (customerRes.data && customerRes.data.result) {
                    this.customers = customerRes.data.result.map(c => ({
                        id: c.id,
                        name: `${c.first_name || ''} ${c.last_name || ''}`.trim(),
                        email: c.email_id
                    }));
                }

                if (productRes.data && productRes.data.result) {
                    this.products = productRes.data.result.map(p => ({
                        id: p.id,
                        name: p.name || '',
                        sku: p.sku || ''
                    }));
                }
            } catch (err) {
                if (requestId === this.searchRequestId) {
                    this.customers = [];
                    this.products = [];
                }
                console.error('Error fetching command palette search results', err);
            } finally {
                if (requestId === this.searchRequestId) {
                    this.isSearching = false;
                }
            }
        },

        performSearch() {
            this.selectedIndex = 0;
            if (this.searchTimer) {
                clearTimeout(this.searchTimer);
                this.searchTimer = null;
            }

            const query = this.searchQuery.trim();
            const requestId = ++this.searchRequestId;
            this.customers = [];
            this.products = [];

            if (query.length < 2) {
                this.isSearching = false;
                return;
            }
            this.isSearching = true;
            this.searchTimer = setTimeout(() => {
                this.fetchSearchResults(query, requestId);
            }, 250);
        },

        getItemIndex(section, index) {
            let offset = 0;

            if (section === 'pages') {
                return index;
            } else if (section === 'customers') {
                offset = this.filteredResults.pages.length;
                return offset + index;
            } else if (section === 'products') {
                offset = this.filteredResults.pages.length + this.filteredResults.customers.length;
                return offset + index;
            }

            return 0;
        },

        navigateResults(direction) {
            const maxIndex = this.totalResultsCount - 1;

            if (maxIndex < 0) return;

            this.selectedIndex = (this.selectedIndex + direction + this.totalResultsCount) % this.totalResultsCount;
        },

        selectResult() {
            const results = this.filteredResults;

            if (this.selectedIndex < results.pages.length) {
                // Page selected
                this.navigateTo(results.pages[this.selectedIndex].path);
            } else if (this.selectedIndex < results.pages.length + results.customers.length) {
                // Customer selected
                const customerIndex = this.selectedIndex - results.pages.length;
                this.navigateTo(customerEditPath(results.customers[customerIndex].id));
            } else {
                // Product selected
                const productIndex = this.selectedIndex - results.pages.length - results.customers.length;
                if (!results.products[productIndex]) return;
                this.navigateTo(productDetailPath(results.products[productIndex].id));
            }
        },

        navigateTo(path) {
            this.closeCommandPalette();
            this.$router.push(path);
        },

        navigateToCustomer(id) {
            this.navigateTo(customerEditPath(id));
        },

        navigateToProduct(id) {
            this.navigateTo(productDetailPath(id));
        },

        // Scanner panel methods
        openScannerPanel() {
            this.showScannerPanel = true;
        },

        closeScannerPanel() {
            this.showScannerPanel = false;
        }
    },
    mounted() {
        // Add keyboard shortcut listener
        window.addEventListener('keydown', this.handleKeyDown);
    },
    beforeDestroy() {
        // Remove keyboard shortcut listener
        window.removeEventListener('keydown', this.handleKeyDown);
        if (this.searchTimer) {
            clearTimeout(this.searchTimer);
            this.searchTimer = null;
        }
    },
    components: { ScannerStatus, CommandPalette }, // ThemeSwitch removed to hide dark/light toggle
}
</script>

<style lang="scss" scoped>
@use "sass:color";
@import "@/assets/variables.scss";

.navbar-header-container {
    background: $lightCardBg;
    border-bottom: 1px solid $lightBorderColor; /* Use border instead of heavy shadow for a single elevation */
    box-shadow: none;
    position: sticky;
    top: 0;
    z-index: 150;
    height: $headerHeight;
    backdrop-filter: none; /* Avoid any blur that could create a haze */
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

    /* Remove left divider so header and content align perfectly */
    border-left: none;

    &.dark {
        background: $darkCardBg;
        border-color: $darkBorderColor;
        box-shadow: none;
        backdrop-filter: none;
    }

    .container {
        /* Match page container width and gutters */
        max-width: $containerMaxWidth;
        margin-left: auto;
        margin-right: auto;
        padding: 0 $containerGutter;
        height: 100%;
    }
}

/* Remove margin to eliminate space between header and content */
.page-title-container {
    padding: 0;
    border-bottom: none;
    margin-top: 0;
    display: none; /* Keep hidden to avoid double bars */

    .dark & {
        border-color: $darkBorderColor;
    }

    .page-title {
        margin: 0;
        padding: 0;
        font-size: 18px;
        font-weight: 600;
        color: $primaryColor;

        .dark & {
            color: $secondaryColorDark;
        }
    }
}

.navbar-header {
    padding: 0;
    height: 100%; /* Fill the entire header height */

    .header-link-container {
        padding: 0;
        display: grid;
        grid-template-columns: 1fr auto 1fr;
        align-items: center;
        height: 100%; /* Fill entire height */
    }
}

.sidebar-toggle-area {
    justify-self: flex-start;
}

.search-container {
    justify-self: center;
}

.sidebar-expand-button {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    padding: 8px 12px;
    border-radius: 8px;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    background-color: rgba(13, 45, 44, 0.05);
    border: 1px solid rgba(13, 45, 44, 0.1);

    &:hover {
        background-color: rgba(13, 45, 44, 0.1);
        border-color: rgba(13, 45, 44, 0.2);
        transform: translateX(2px);
    }

    i {
        font-size: 20px;
        color: $primaryColor;
        transition: all 0.3s ease;
    }

    .expand-text {
        font-size: 13px;
        font-weight: 600;
        color: $primaryColor;
        transition: all 0.3s ease;
    }

    &:hover {
        i, .expand-text {
            color: $secondaryColor;
        }
    }

    .dark & {
        background-color: rgba(186, 142, 88, 0.15);
        border-color: rgba(186, 142, 88, 0.25);

        i, .expand-text {
            color: $secondaryColorDark;
        }

        &:hover {
            background-color: rgba(186, 142, 88, 0.25);
            border-color: rgba(186, 142, 88, 0.4);

            i, .expand-text {
                color: $darkTextColor;
            }
        }
    }
}

.search-trigger {
    display: flex;
    align-items: center;
    background-color: $lightHoverColor;
    border: 1px solid $lightBorderColor;
    border-radius: 10px;
    padding: 8px 16px;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    width: 320px;
    height: 40px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    font: inherit;
    text-align: left;

    &:hover {
        background-color: white;
        border-color: rgba(13, 45, 44, 0.2);
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
        transform: translateY(-1px);
    }

    &:focus-within {
        border-color: $primaryColor;
        box-shadow: 0 0 0 3px rgba(13, 45, 44, 0.1);
    }

    i {
        color: $primaryColor;
        opacity: 0.8;
        font-size: 18px;
        margin-right: 12px;
        transition: all 0.3s ease;
    }

    .search-placeholder {
        color: $textColor;
        opacity: 0.7;
        font-size: 14px;
        flex: 1;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        font-weight: 500;
    }

    .keyboard-shortcut {
        display: flex;
        align-items: center;
        gap: 2px;

        span {
            background-color: rgba(13, 45, 44, 0.08);
            color: $primaryColor;
            opacity: 0.9;
            font-size: 11px;
            padding: 3px 6px;
            border-radius: 4px;
            font-weight: 600;
            border: 1px solid rgba(13, 45, 44, 0.15);
            min-width: 20px;
            text-align: center;
        }
    }

    .dark & {
        background-color: rgba(21, 38, 35, 0.6);
        border-color: $darkBorderColor;

        &:hover {
            background-color: rgba(21, 38, 35, 0.8);
            border-color: rgba(186, 142, 88, 0.4);
        }

        i {
            color: $secondaryColorDark;
        }

        .search-placeholder {
            color: $darkTextColor;
        }

        .keyboard-shortcut span {
            background-color: rgba(186, 142, 88, 0.2);
            color: $secondaryColorDark;
            border-color: rgba(186, 142, 88, 0.3);
        }
    }
}

.scanner-container {
    margin-right: 20px;

    // Make scanner status compact in header
    :deep(.scanner-status-widget) {
        font-size: 12px;
        padding: 6px 8px;
        min-width: 120px;

        .scanner-header {
            margin-bottom: 4px;
        }

        .scanner-title {
            font-size: 12px;

            .scanner-icon {
                font-size: 14px;
            }
        }

        .scanner-controls .btn {
            padding: 2px 6px;
            font-size: 10px;
        }

        .status-indicator {
            .status-dot {
                width: 6px;
                height: 6px;
            }

            .status-text {
                font-size: 10px;
            }
        }
    }
}

.header-actions {
    display: flex;
    align-items: center;
    gap: 16px;
    height: 100%;
    justify-self: flex-end;

    .divider {
        width: 1px;
        height: 28px;
        background: linear-gradient(to bottom, transparent, $lightBorderColor, transparent);

        .dark & {
            background: linear-gradient(to bottom, transparent, $darkBorderColor, transparent);
        }
    }
}

.profile-wrapper {
    height: 100%;
    display: flex;
    align-items: center;

    .profile-info {
        display: flex;
        align-items: center;
        gap: 12px;
        text-decoration: none;
        height: 100%;
        padding: 6px 8px;
        border-radius: 8px;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

        &:hover {
            background-color: rgba(13, 45, 44, 0.08);
            transform: translateY(-1px);

            .avatar-container {
                transform: scale(1.05);
                box-shadow: 0 4px 8px rgba(13, 45, 44, 0.2);
            }
        }

        .avatar-container {
            width: 36px;
            height: 36px;
            border-radius: 50%;
            background: linear-gradient(135deg, $primaryColor 0%, color.adjust($primaryColor, $lightness: -10%) 100%);
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-weight: 700;
            font-size: 14px;
            transition: all 0.3s ease;
            box-shadow: 0 2px 4px rgba(13, 45, 44, 0.15);
            border: 2px solid rgba(255, 255, 255, 0.2);

            .dark & {
                background: linear-gradient(135deg, $secondaryColorDark 0%, color.adjust($secondaryColorDark, $lightness: -10%) 100%);
                border-color: rgba(186, 142, 88, 0.3);
            }
        }
    }
}

.logout-button {
    background: none;
    border: 1px solid transparent;
    cursor: pointer;
    color: $accentColor;
    opacity: 0.8;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    padding: 8px;
    border-radius: 8px;
    height: 36px;
    width: 36px;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
        background-color: rgba(141, 68, 59, 0.1);
        color: color.adjust($accentColor, $lightness: -10%);
        opacity: 1;
        transform: scale(1.05);
        border-color: rgba(141, 68, 59, 0.2);
    }

    &:active {
        transform: scale(0.95);
    }

    i {
        font-size: 20px;
        transition: transform 0.3s ease;
    }

    &:hover i {
        transform: rotate(-15deg);
    }

    .dark & {
        color: $accentColorDark;

        &:hover {
            background-color: rgba(141, 68, 59, 0.15);
            color: color.adjust($accentColorDark, $lightness: 10%);
            border-color: rgba(141, 68, 59, 0.3);
        }
    }
}

/* Scanner indicator */
.scanner-indicator {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s ease;
    margin-right: 8px;
    border: 0;
    background: transparent;

    &:hover {
        background-color: rgba(0, 0, 0, 0.05);
    }

    .material-icons {
        font-size: 18px;

        &.scanner-connected { color: $successColor; }
        &.scanner-active { color: $warningColor; animation: pulse 2s infinite; }
        &.scanner-disconnected { color: rgba($textColor, 0.5); }
    }

    .dark & {
        &:hover {
            background-color: rgba(255, 255, 255, 0.1);
        }
        .material-icons.scanner-connected { color: $successColorDark; }
        .material-icons.scanner-active { color: $warningColorDark; }
        .material-icons.scanner-disconnected { color: rgba($darkTextColor, 0.6); }
    }
}

@media (max-width: 768px) {
    .navbar-header-container {
        min-width: 0;
    }

    .navbar-header-container > .container,
    .page-title-container > .container {
        max-width: 100%;
        padding-left: 12px;
        padding-right: 12px;
    }

    .navbar-header {
        .header-link-container {
            grid-template-columns: auto minmax(0, 1fr) auto;
            gap: 8px;
        }
    }

    .sidebar-toggle-area,
    .search-container,
    .header-actions {
        min-width: 0;
    }

    .sidebar-expand-button {
        width: 40px;
        height: 40px;
        justify-content: center;
        padding: 0;

        .expand-text {
            display: none;
        }
    }

    .search-trigger {
        width: 100%;
        min-width: 44px;
        max-width: 220px;
        padding: 8px 10px;

        i {
            margin-right: 8px;
        }
    }

    .header-actions {
        gap: 8px;

        .divider {
            display: none;
        }
    }

    .scanner-indicator,
    .logout-button,
    .profile-wrapper .profile-info {
        width: 36px;
        height: 36px;
        padding: 0;
        justify-content: center;
    }

    .scanner-panel-overlay {
        justify-content: center;
        padding: 72px 12px 12px;
    }

    .scanner-panel {
        width: 100%;
        max-width: 400px;
    }
}

@media (max-width: 480px) {
    .search-trigger {
        width: 44px;
        padding: 0;
        justify-content: center;

        i {
            margin-right: 0;
        }

        .search-placeholder,
        .keyboard-shortcut {
            display: none;
        }
    }

    .header-actions {
        gap: 4px;
    }

}

@keyframes pulse {
    0%, 100% {
        opacity: 1;
    }
    50% {
        opacity: 0.5;
    }
}

/* Scanner panel */
.scanner-panel-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: flex-start;
    justify-content: flex-end;
    z-index: 1400;
    padding: 80px 20px 20px 20px; // Account for header height
}

.scanner-panel {
    width: 400px;
    background-color: $lightCardBg;
    border-radius: 12px;
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
    overflow: hidden;

    .dark & {
        background: linear-gradient(135deg, $darkCardBg 0%, color.adjust($darkCardBg, $lightness: -2%) 100%);
        box-shadow: 0 8px 30px rgba(0, 0, 0, 0.4);
        border: 1px solid $darkBorderColor;
    }
}

/* Fade transition */
.fade-enter-active, .fade-leave-active {
    transition: opacity 0.2s ease;
}
.fade-enter, .fade-leave-to {
    opacity: 0;
}
</style>
